import type { ConnectorId } from '@research-assistant/shared';
import { UnplugIcon } from 'lucide-react';
import type { JSX } from 'react';

import { cn } from '@/lib/utils.js';

import confluenceIcon from '../../../assets/connectors/confluence.svg';
import databricksIcon from '../../../assets/connectors/databricks.svg';
import dynamics365Icon from '../../../assets/connectors/dynamics365.svg';
import githubIcon from '../../../assets/connectors/github.svg';
import gitlabIcon from '../../../assets/connectors/gitlab.svg';
import jiraIcon from '../../../assets/connectors/jira.svg';
import outlookIcon from '../../../assets/connectors/outlook.svg';
import reportPortalIcon from '../../../assets/connectors/reportportal.svg';
import sharepointIcon from '../../../assets/connectors/sharepoint.svg';
import teamsIcon from '../../../assets/connectors/teams.svg';
import testrailIcon from '../../../assets/connectors/testrail.svg';
import xrayIcon from '../../../assets/connectors/xray.png';
import zephyrIcon from '../../../assets/connectors/zephyr.svg';

/**
 * App-tile rendering of brand icons. Each connector ID maps to a vendor
 * background color + the original monochromatic brand SVG (from simple-icons,
 * CC0). The SVG is rendered as an `<img>` and inverted to white via CSS
 * filter — works inside the Teams iframe / strict CSP contexts where
 * `mask-image: url(...)` against a Vite-fingerprinted asset URL silently
 * fails to render in some browsers, leaving a blank tile.
 *
 * Salesforce is an exception: it renders as a colored cloud on a white
 * tile per brand guidance ("blue cloud on white"). Drawn via inline SVG
 * with an explicit `fill` so we don't rely on the brittle filter chain
 * that would otherwise be needed to recolor an <img>.
 *
 * Multicolor official marks (Outlook, Microsoft Teams, Dynamics 365) use the `image` kind:
 * the SVG is rendered as an `<img>` with NO CSS filter, so the brand colors
 * survive.
 */

interface FilterBrand {
  kind: 'filter';
  bg: string;
  icon: string;
}
interface InlineBrand {
  kind: 'inline';
  bg: string;
  fg: string;
  border: string;
  path: string;
}
interface ImageBrand {
  kind: 'image';
  bg: string;
  border: string;
  icon: string;
  /**
   * Share of the tile the mark occupies. Defaults to 72%, which suits a bare mark that needs the
   * tile's own padding; a mark that already carries its own tile (Xray) fills it at 100%.
   */
  size?: string;
}
type BrandStyle = FilterBrand | InlineBrand | ImageBrand;

const SALESFORCE_PATH =
  'M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8';

/**
 * A plain BrowserStack glyph — a ring around a dot on the brand orange. It is not the vendor's mark,
 * which this repo does not carry; it only gives the tile a recognizable colour and shape.
 */
const BROWSERSTACK_PATH =
  'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a6 6 0 1 1 0 12 6 6 0 0 1 0-12zm0 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z';

/** Official Azure DevOps mark (simple-icons `azuredevops`, CC0) on Azure blue. */
const AZURE_DEVOPS_PATH =
  'M0 8.877L2.247 5.91l8.405-3.416V.022l7.37 5.393L2.966 8.338v8.225L0 15.707zm24-4.45v14.651l-5.753 4.9-9.303-3.057v3.056l-5.978-4.529 15.057 1.798V5.415z';

/** A plain chart glyph on Power BI yellow, rather than a vendor logo asset. */
const POWERBI_PATH = 'M4 13h4v8H4zm6-6h4v14h-4zm6-4h4v18h-4z';

/**
 * Keyed by string, not the strict `ConnectorId` union: the server catalog can add a connector
 * (e.g. `xray`) ahead of `BuiltinConnectorId` widening to include it — same as `browserstack` /
 * `cursor`, whose ids the catalog also returns without a matching literal in that union. A brand
 * with no entry here still renders (the generic `UnplugIcon` fallback below).
 */
const BRANDS: Partial<Record<string, BrandStyle>> = {
  jira: { kind: 'filter', bg: '#2684FF', icon: jiraIcon },
  confluence: { kind: 'filter', bg: '#2684FF', icon: confluenceIcon },
  salesforce: {
    kind: 'inline',
    bg: '#ffffff',
    fg: '#00A1E0',
    border: 'rgba(0,0,0,0.08)',
    path: SALESFORCE_PATH,
  },
  testrail: { kind: 'filter', bg: '#65C179', icon: testrailIcon },
  // Xray ships its mark as a white bolt already sitting on its own green rounded tile, so it is
  // rendered whole at native colors — inverting it to white the way the monochrome marks are done
  // would erase the bolt into the tile.
  xray: {
    kind: 'image',
    bg: 'transparent',
    border: 'transparent',
    icon: xrayIcon,
    size: '100%',
  },
  zephyr: { kind: 'filter', bg: '#6554C0', icon: zephyrIcon },
  outlook: {
    kind: 'image',
    bg: '#ffffff',
    border: 'rgba(0,0,0,0.08)',
    icon: outlookIcon,
  },
  teams: {
    kind: 'image',
    bg: '#ffffff',
    border: 'rgba(0,0,0,0.08)',
    icon: teamsIcon,
  },
  azure_devops: {
    kind: 'inline',
    bg: '#0078D4',
    fg: '#ffffff',
    border: 'transparent',
    path: AZURE_DEVOPS_PATH,
  },
  github: { kind: 'filter', bg: '#181717', icon: githubIcon },
  browserstack: {
    kind: 'inline',
    bg: '#E66F32',
    fg: '#ffffff',
    border: 'transparent',
    path: BROWSERSTACK_PATH,
  },
  gitlab: { kind: 'filter', bg: '#FC6D26', icon: gitlabIcon },
  sharepoint: { kind: 'filter', bg: '#038387', icon: sharepointIcon },
  powerbi: {
    kind: 'inline',
    bg: '#F2C811',
    fg: '#252423',
    border: 'transparent',
    path: POWERBI_PATH,
  },
  databricks: { kind: 'filter', bg: '#FF3621', icon: databricksIcon },
  /**
   * Report Portal's own mark (`logo-icon-inline.svg` from reportportal/service-ui, Apache-2.0) at its
   * native cyan, on a white tile like the Microsoft connectors. Its licence and notice are in
   * `assets/connectors/reportportal.LICENSE.txt`.
   */
  reportportal: {
    kind: 'image',
    bg: '#ffffff',
    border: 'rgba(0,0,0,0.08)',
    icon: reportPortalIcon,
  },
  /**
   * Microsoft's official Dynamics 365 mark, kept at native colors like the other Microsoft
   * connectors. It is the only portrait mark in the set, so it takes a larger share of the tile
   * than the square marks' 72% default: fitted by its height, at 72% the letterboxing left it
   * visibly lighter than the Teams tile beside it.
   */
  dynamics365: {
    kind: 'image',
    bg: '#ffffff',
    border: 'rgba(0,0,0,0.08)',
    icon: dynamics365Icon,
    size: '82%',
  },
};

export function ConnectorIcon({
  id,
  className,
}: {
  id: ConnectorId;
  className?: string;
}): JSX.Element {
  const brand = BRANDS[id];
  if (!brand) {
    return <UnplugIcon className={className} aria-hidden="true" />;
  }
  if (brand.kind === 'image') {
    return (
      <div
        className={cn(
          'inline-flex items-center justify-center rounded-lg',
          className,
        )}
        style={{
          backgroundColor: brand.bg,
          border: `1px solid ${brand.border}`,
        }}
        aria-hidden="true"
      >
        <img
          src={brand.icon}
          alt=""
          style={{ width: brand.size ?? '72%', height: brand.size ?? '72%' }}
        />
      </div>
    );
  }
  if (brand.kind === 'inline') {
    return (
      <div
        className={cn(
          'inline-flex items-center justify-center rounded-lg',
          className,
        )}
        style={{
          backgroundColor: brand.bg,
          border: `1px solid ${brand.border}`,
        }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" fill={brand.fg} className="size-[72%]">
          <path d={brand.path} />
        </svg>
      </div>
    );
  }
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-lg',
        className,
      )}
      style={{ backgroundColor: brand.bg }}
      aria-hidden="true"
    >
      <img
        src={brand.icon}
        alt=""
        className="size-[56%]"
        style={{ filter: 'brightness(0) invert(1)' }}
      />
    </div>
  );
}
